import { useState, useEffect, memo, useRef, useMemo, useCallback } from "react";
import { SqButton } from "@/components/squircle-ui/button";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/components/ui/tooltip";
import {
  RiArrowDownWideLine,
  RiBarChart2Fill,
  RiCameraLine,
  RiLink,
  RiSearchLine,
} from "@remixicon/react";
import {
  IconInfoCircle,
  IconPhoto,
  IconMovie,
  IconChartCircles,
} from "@tabler/icons-react";
import { Cell, Pie, PieChart } from "recharts";
import { Separator } from "@/components/ui/separator";
import { Fragment } from "react";
import { LineChart } from "@/components/ui/tremor-charts/LineChart";
import ReportDateSelector from "../../competitorReports/ReportDateSelector";
import { fetchCompetitorOverview } from "../../../apiService";
import {
  CompetitorOverviewResponse,
  AdDistribution,
  LandingPage,
  GraphData,
} from "../../../types";
import { ReportDate } from "@/types";
import { useGlobalStore } from "@/GlobalStore";
import SelectDropdown from "@/components/common/dropdown/SelectDropdown";
import WaveLoader from "@/components/common/loaders/WaveLoader";
import NothingToShow from "@/components/common/emptyStates/NothingToShow";
import { formatGraphDateLabel } from "@/utils/dateUtils";
import { cn } from "@/lib/utils";
import CompetitorsEmpty from "@/components/common/emptyStates/CompetitorsEmpty";

type Props = {
  selectedBrandIds: string[];
  filters: Array<{
    key: string;
    conditions: Array<{
      operator: string;
      value: string;
    }>;
  }>;
  location: string | null;
  brands?: Array<{
    id: string;
    name: string;
    icon_url?: string;
  }>;
  selectedDate: ReportDate | null;
  onDateChange: (date: ReportDate | null) => void;
  refetchToken: number;
};

// Active Ads Section Component (adapted from AccountOverview)
interface ActiveAdsSectionProps {
  distribution: AdDistribution;
}

const ActiveAdsSection: React.FC<ActiveAdsSectionProps> = ({ distribution }) => {
  const chartData = distribution.data.map((item, index) => {
    // Define colors for different media types
    const colors = ["#EC4899", "#10B981", "#6B7280", "#F59E0B", "#8B5CF6"];
    return {
      name: item.label,
      value: item.value,
      percentage: item.percent,
      color: colors[index % colors.length],
    };
  });

  // Calculate total from distribution data
  const total = distribution.total;

  return (
    <div className="w-[233px] h-full bg-white rounded-[12px] p-[8px] squircle squircle-radius-[12px] squircle-outline-[1px] squircle-outline-color-border-gray">
      {!distribution || !distribution.data || distribution.data.length === 0 ? (
        <div className="h-full w-full flex items-center justify-center">
          <NothingToShow />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-[36px]">
            <div className="pl-[8px] pt-[5px] flex gap-2">
              <RiCameraLine size={14} className="text-brand-primary-400" />
              <h4 className="text-[12px] font-medium text-brand-primary-800">
                {distribution.label}
              </h4>
            </div>
          </div>

          <div className="flex flex-col items-center gap-[26px]">
            {/* Recharts Donut Chart */}
            <div className="relative">
              <PieChart width={168} height={84}>
                <defs>
                  {/* Gradient for Images (Pink) - Based on paint0_linear_36773_1250 */}
                  <linearGradient
                    id="gradientImages"
                    x1="84"
                    y1="0"
                    x2="7.5"
                    y2="84"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#FF53A0" />
                    <stop offset="1" stopColor="#FF9DC9" />
                  </linearGradient>
                  {/* Gradient for Videos (Green) - Based on paint1_linear_36773_1250 */}
                  <linearGradient
                    id="gradientVideos"
                    x1="84"
                    y1="0"
                    x2="160"
                    y2="67"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#0EC28C" />
                    <stop offset="1" stopColor="#01A070" />
                  </linearGradient>
                  {/* Gradient for Others (Gray) - Custom similar gradient */}
                  <linearGradient
                    id="gradientOthers"
                    x1="84"
                    y1="0"
                    x2="7.5"
                    y2="84"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#F6F6F6" />
                    <stop offset="1" stopColor="#F6F6F6" />
                  </linearGradient>
                </defs>
                <Pie
                  data={chartData}
                  cx={80}
                  cy={84}
                  startAngle={180}
                  endAngle={0}
                  innerRadius={65}
                  outerRadius={84}
                  paddingAngle={1}
                  dataKey="value"
                >
                  {chartData.map((entry) => {
                    const gradientId = `gradient${entry.name}`;
                    return (
                      <Cell key={`cell-${entry.name}`} fill={`url(#${gradientId})`} />
                    );
                  })}
                </Pie>
              </PieChart>
              {/* Center label */}
              <div className="absolute inset-0 flex flex-col items-center justify-end">
                <span className="text-[14px] font-normal text-brand-primary-800">
                  {total.toLocaleString()}
                </span>
                <span className="text-[10px] text-brand-primary-800">Ads Running</span>
              </div>
            </div>

            {/* Legend */}
            <div className="flex flex-col gap-[6px] w-full px-4">
              {chartData.map((item) => (
                <div
                  key={item.name}
                  className="w-full flex gap-4 items-center justify-between"
                >
                  <div className="flex items-center gap-[8px]">
                    {/* Dynamic icon mapping based on item name */}
                    {item.name.toLowerCase().includes("image") && (
                      <IconPhoto size={14} className="text-[#FF4699]" />
                    )}
                    {item.name.toLowerCase().includes("video") && (
                      <IconMovie size={14} className="text-[#018C62]" />
                    )}
                    {!item.name.toLowerCase().includes("image") &&
                      !item.name.toLowerCase().includes("video") && (
                        <IconChartCircles size={14} className="text-brand-primary-400" />
                      )}
                    <span className="text-[12px] text-brand-primary-800">
                      {item.name}
                    </span>
                  </div>
                  <div className="flex-1 flex items-center gap-[8px] justify-end overflow-hidden">
                    <span className="text-[12px] font-medium text-brand-primary-800 truncate">
                      {item.value.toLocaleString()}
                    </span>
                    <span className="text-[12px] text-brand-primary-600">
                      {item.percentage}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Landing Pages Section Component (adapted from PerformanceSection)
interface LandingPagesSectionProps {
  landingPages: LandingPage[];
}

const LandingPagesSection: React.FC<LandingPagesSectionProps> = ({ landingPages }) => {
  const [isScrollable, setIsScrollable] = useState(false);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const optionsContainerRef = useRef<HTMLDivElement>(null);

  // Check if content is scrollable
  useEffect(() => {
    const checkScrollable = () => {
      if (optionsContainerRef.current) {
        const { scrollHeight, clientHeight } = optionsContainerRef.current;
        setIsScrollable(scrollHeight > clientHeight);
      }
    };

    // Check initially and on items change
    checkScrollable();

    // Add resize observer to handle dynamic changes
    const resizeObserver = new ResizeObserver(checkScrollable);
    if (optionsContainerRef.current) {
      resizeObserver.observe(optionsContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [landingPages]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setIsScrolledToBottom(
      e.currentTarget.scrollTop + e.currentTarget.clientHeight >=
        e.currentTarget.scrollHeight - 10,
    );
  };

  return (
    <div className="w-1/3 max-w-[446px] h-full bg-white rounded-[12px] flex flex-col items-start gap-[18px] px-[6px] pt-[12px] pb-[6px] squircle squircle-radius-[12px] squircle-outline-[1px] squircle-outline-color-border-gray">
      {/* Title */}
      <div className="flex items-center gap-[8px] pl-1">
        <RiLink size={14} className="text-brand-primary-400" />
        <h4 className="text-[12px] font-medium text-brand-primary-800">Landing Pages</h4>
      </div>
      {/* Body */}
      <div className="relative w-full flex-1 overflow-hidden">
        {landingPages.length === 0 ? (
          <div className="w-full h-full flex items-center justify-center">
            <NothingToShow />
          </div>
        ) : (
          <div
            ref={optionsContainerRef}
            onScroll={handleScroll}
            className="w-full h-full overflow-y-auto flex flex-col items-start gap-[12px] px-[8px] py-[12px] rounded-[8px] squircle squircle-radius-[8px] squircle-outline-[1px] squircle-outline-color-border-gray"
          >
            {landingPages.map((page, index) => (
              <Fragment key={index}>
                <div className="w-full flex items-center justify-between gap-[8px]">
                  <a
                    href={page.label}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[11px] text-brand-primary-800 flex-1 leading-[14px] truncate hover:text-brand-primary-600 hover:underline cursor-pointer"
                    title={`Click to open ${page.label} in new tab`}
                  >
                    {page.label}
                  </a>
                  <div className="flex items-center gap-[8px] flex-shrink-0">
                    <span className="text-[11px] text-brand-primary-800 whitespace-nowrap">
                      {page.value.toLocaleString()}
                    </span>
                    <span className="text-[11px] text-brand-primary-600 whitespace-nowrap">
                      {page.percent.toFixed(1)}%
                    </span>
                  </div>
                </div>
                {index < landingPages.length - 1 && (
                  <Separator className="w-full bg-border-gray/60" />
                )}
              </Fragment>
            ))}
          </div>
        )}
        {isScrollable && !isScrolledToBottom && (
          <div
            className="absolute bottom-0 left-0 right-0 h-[46px] pointer-events-none"
            style={{
              background:
                "linear-gradient(0deg, #FFF 0%, rgba(255, 255, 255, 0.00) 100%)",
            }}
          />
        )}
      </div>
    </div>
  );
};

// Custom Legend Component for Graph with Brand Icons
interface CustomLegendProps {
  payload?: Array<{
    value: string;
    color: string;
  }>;
  brands?: Array<{
    id: string;
    name: string;
    icon_url?: string;
  }>;
  onClick?: (name: string) => void;
  activeLegend?: string;
  hasLimited?: boolean;
}

const CustomLegend: React.FC<CustomLegendProps> = ({
  payload,
  brands = [],
  onClick,
  hasLimited,
}) => {
  if (!payload || !payload.length) return null;
  const brandMap = new Map<string, { name: string; icon_url?: string }>();
  brands.forEach((brand) => {
    brandMap.set(brand.id, { name: brand.name, icon_url: brand.icon_url });
  });

  return (
    <div className="h-[14px] mb-[18px] flex items-center justify-between">
      {hasLimited && (
        <div className="text-[11px] text-brand-primary-400">Compare up to 7 brands</div>
      )}
      <div className="flex-1 flex flex-wrap gap-[10px] items-center justify-end">
        {payload.map((entry, index) => {
          const brandInfo = brandMap.get(entry.value);

          return (
            <div
              key={index}
              className={`flex items-center gap-[5px]`}
              onClick={() => onClick?.(entry.value)}
            >
              <div
                className="w-[6px] h-[6px] rounded-full flex-shrink-0"
                style={{ backgroundColor: entry.color }}
              />
              {brandInfo?.icon_url ? (
                <img
                  src={brandInfo.icon_url}
                  alt={brandInfo.name}
                  className="w-[14px] h-[14px] rounded-sm object-cover"
                />
              ) : (
                <div className="w-[14px] h-[14px] rounded-full bg-gray-300 flex items-center justify-center">
                  <span className="text-xs text-gray-600 font-medium">
                    {brandInfo?.name.charAt(0).toUpperCase() ||
                      entry.value.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Custom Tooltip Component for Graph with Brand Icons
interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    category: string;
    value: number;
    color: string;
  }>;
  label?: string;
  brands?: Array<{
    id: string;
    name: string;
    icon_url?: string;
  }>;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  label,
  brands = [],
}) => {
  if (!active || !payload || !payload.length) return null;
  const brandMap = new Map<string, { name: string; icon_url?: string }>();
  brands.forEach((brand) => {
    brandMap.set(brand.id, { name: brand.name, icon_url: brand.icon_url });
  });

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
      <p className="text-[12px] font-medium text-brand-primary-800 mb-2">{label}</p>
      <div className="space-y-1">
        {payload.map((entry, index) => {
          const brandInfo = brandMap.get(entry.category);
          return (
            <div key={index} className="flex items-center gap-[6px]">
              <div
                className="w-[6px] h-[6px] rounded-full flex-shrink-0"
                style={{ backgroundColor: entry.color }}
              />
              {brandInfo?.icon_url ? (
                <img
                  src={brandInfo.icon_url}
                  alt={brandInfo.name}
                  className="w-[14px] h-[14px] rounded-full object-cover"
                />
              ) : (
                <div className="w-[14px] h-[14px] rounded-full bg-gray-300 flex items-center justify-center">
                  <span className="text-xs text-gray-600 font-medium">
                    {brandInfo?.name.charAt(0).toUpperCase() ||
                      entry.category.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <span className="text-[12px] text-brand-primary-800">
                {brandInfo?.name || entry.category}:
              </span>
              <span className="text-[12px] font-medium text-brand-primary-800">
                {entry.value.toLocaleString()}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Graph Data Section Component using LineChart
interface GraphDataSectionProps {
  graphData: GraphData;
  dataKey: string;
  brands?: Array<{
    id: string;
    name: string;
    icon_url?: string;
  }>;
  timezone?: string;
}

const GraphDataSection: React.FC<GraphDataSectionProps> = ({
  graphData,
  brands = [],
  dataKey,
  timezone,
}) => {
  const brandLimit = 7;
  // Limit to first 7 brands
  const limitedBrands = useMemo(() => brands.slice(0, brandLimit), [brands]);

  // Helper function to format date labels
  const formatDataPointLabel = useCallback(
    (point: { x: string }) => {
      const intervalRange = graphData.interval_ranges.find(
        (range) => range.label === point.x,
      );
      return intervalRange
        ? formatGraphDateLabel(
            intervalRange.start_date,
            intervalRange.end_date,
            undefined,
            timezone,
          )
        : point.x;
    },
    [graphData.interval_ranges, timezone],
  );

  // Transform graph data for LineChart
  const chartData = graphData.data_points.map((point) => {
    const customLabel = formatDataPointLabel(point);
    const dataPoint: { [key: string]: number | string } = { x: customLabel };

    // Process brands in the same order as limitedBrands
    limitedBrands.forEach((brand) => {
      const brandData = point[brand.id];
      if (brandData && typeof brandData === "object" && brandData !== null) {
        // Use brand ID as the data key to avoid conflicts with duplicate names
        dataPoint[brand.id] = brandData[dataKey as keyof typeof brandData] || 0;
      }
    });

    return dataPoint;
  });

  // Extract all brand IDs as categories in the same order as limitedBrands
  const categories = useMemo(() => {
    return limitedBrands.map((brand) => brand.id);
  }, [limitedBrands]);

  // Value formatter for the chart
  const valueFormatter = (value: number) => {
    const formatter = new Intl.NumberFormat("en-US", {
      notation: "compact",
      compactDisplay: "short",
    });
    return formatter.format(value);
  };

  return (
    <div className="flex-1 h-full w-full overflow-hidden flex flex-col gap-[18px] bg-white rounded-[12px] pt-[12px] pb-[6px] px-[6px] squircle squircle-radius-[12px] squircle-outline-[1px] squircle-outline-color-border-gray">
      <div className="flex items-center justify-between">
        <div className="pl-[4px] flex items-center gap-2">
          <RiSearchLine size={14} className="text-brand-primary-400" />
          <h4 className="text-[12px] font-medium text-brand-primary-800">
            Competitor Comparison
          </h4>
        </div>
      </div>

      <div className="flex-1 overflow-hidden w-full px-2 py-2 rounded-[8px] squircle squircle-radius-[8px] squircle-outline-[1px] squircle-outline-color-border-gray">
        {chartData.length > 0 && categories.length > 0 ? (
          <LineChart
            className="h-full w-full"
            data={chartData}
            index="x"
            categories={categories}
            valueFormatter={valueFormatter}
            showXAxis={true}
            showYAxis={true}
            showGridLines={true}
            showTooltip={true}
            showLegend={true}
            startEndOnly={false}
            allowDecimals={false}
            strokeColors={["#10B981", "#EC4899", "#F59E0B", "#8B5CF6", "#6B7280"]}
            customTooltip={(props) => <CustomTooltip {...props} brands={limitedBrands} />}
            chartMargin={{
              left: 20,
            }}
            customLegend={(props) => (
              <CustomLegend
                {...props}
                brands={limitedBrands}
                hasLimited={limitedBrands.length < brands.length}
              />
            )}
          />
        ) : (
          <div className="h-full flex items-center justify-center">
            <NothingToShow />
          </div>
        )}
      </div>
    </div>
  );
};

const CompetitorAdsOverview = ({
  selectedBrandIds,
  filters,
  location,
  brands,
  selectedDate,
  onDateChange,
  refetchToken,
}: Props) => {
  const selectedAccount = useGlobalStore((state) => state.selectedAccount);
  // Initialize states from localStorage
  const [isExpanded, setIsExpanded] = useState<boolean>(() => {
    const saved = localStorage.getItem("competitor-overview-expanded");
    return saved !== null ? (JSON.parse(saved) as boolean) : true;
  });

  const [overviewData, setOverviewData] = useState<CompetitorOverviewResponse | null>(
    null,
  );

  const [selectedOverviewItem, setSelectedOverviewItem] = useState<string>(() => {
    const saved = localStorage.getItem("competitor-overview-selected-item");
    return saved || "";
  });

  const [loading, setLoading] = useState(true);

  // Track mounted state to prevent state updates after unmount
  const isMountedRef = useRef(true);
  // Track abort controller for request cancellation
  const abortControllerRef = useRef<AbortController | null>(null);

  const toggleExpanded = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    localStorage.setItem("competitor-overview-expanded", JSON.stringify(newExpanded));
  };

  const fetchOverviewData = useCallback(
    async (showLoader: boolean = true) => {
      if (!selectedAccount?.id || selectedBrandIds.length === 0) {
        setOverviewData(null);
        return;
      }
      if (selectedBrandIds.some((id) => id.includes("optimistic"))) {
        return;
      }
      if (showLoader) {
        setLoading(true);
      }
      try {
        const response = await fetchCompetitorOverview({
          accountId: selectedAccount.id,
          date: {
            label: selectedDate?.label || "Custom",
            start_date: selectedDate?.start_date || "",
            end_date: selectedDate?.end_date || "",
          },
          brand_ids: selectedBrandIds,
          filters: filters,
          location: location,
        });

        // Only update state if component is still mounted
        if (!isMountedRef.current) return;

        setOverviewData(response);
        // Set the first item as selected by default if no saved selection exists
        if (response && response.length > 0 && !selectedOverviewItem) {
          const firstItemKey = response[0]?.key;
          setSelectedOverviewItem(firstItemKey);
          localStorage.setItem("competitor-overview-selected-item", firstItemKey);
        }
      } catch (error) {
        if (!isMountedRef.current) return;
        console.error("Failed to fetch competitor overview:", error);
        setOverviewData(null);
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      filters,
      location,
      selectedAccount?.id,
      selectedBrandIds,
      selectedDate?.end_date,
      selectedDate?.label,
      selectedDate?.start_date,
    ],
  );

  // Use refs to track previous values and only fetch when they actually change
  const prevPropsRef = useRef({ selectedBrandIds, filters, location });
  const prevDateRef = useRef(selectedDate);

  useEffect(() => {
    const prevProps = prevPropsRef.current;
    const prevDate = prevDateRef.current;

    // Check if props actually changed
    const propsChanged =
      prevProps.selectedBrandIds.length !== selectedBrandIds.length ||
      prevProps.selectedBrandIds.some((id, index) => id !== selectedBrandIds[index]) ||
      prevProps.filters.length !== filters.length ||
      prevProps.filters.some((filter, index) => {
        const prevFilter = prevProps.filters[index];
        return (
          !prevFilter ||
          filter.key !== prevFilter.key ||
          filter.conditions.length !== prevFilter.conditions.length ||
          filter.conditions.some((condition, condIndex) => {
            const prevCondition = prevFilter.conditions[condIndex];
            return (
              !prevCondition ||
              condition.operator !== prevCondition.operator ||
              condition.value !== prevCondition.value
            );
          })
        );
      }) ||
      prevProps.location !== location;

    // Check if date changed
    const dateChanged =
      prevDate?.label !== selectedDate?.label ||
      prevDate?.start_date !== selectedDate?.start_date ||
      prevDate?.end_date !== selectedDate?.end_date;

    // Only fetch if something actually changed
    if (propsChanged || dateChanged) {
      void fetchOverviewData();

      // Update refs with current values
      prevPropsRef.current = { selectedBrandIds, filters, location };
      prevDateRef.current = selectedDate;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrandIds, filters, location, selectedDate]);

  useEffect(() => {
    void fetchOverviewData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchToken]);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Cancel any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  // Determine if CompetitorsEmpty is being shown
  const showCompetitorsEmpty = useMemo(() => {
    if (!overviewData) return true;
    const selectedItem = overviewData.find((item) => item.key === selectedOverviewItem);
    if (!selectedItem) return true;
    return !selectedItem.data || Object.keys(selectedItem.data).length === 0;
  }, [overviewData, selectedOverviewItem]);

  // Show nudge dot when expanded and CompetitorsEmpty is shown
  const showNudgeDot = isExpanded && showCompetitorsEmpty && !loading;

  if (!overviewData && loading) {
    return (
      <div
        className={cn(
          "flex items-center justify-center gap-4 p-[8px] rounded-[12px] squircle squircle-radius-[16px] squircle-outline-color-border-gray squircle-outline-[1px] bg-white overflow-hidden",
          isExpanded ? "h-[331px]" : "h-[48px]",
        )}
      >
        <WaveLoader className="w-[100px]" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div
        className={`flex flex-col gap-4 p-[8px] rounded-[12px] squircle squircle-radius-[16px] squircle-outline-color-border-gray squircle-outline-[1px] bg-white`}
      >
        {/* Header with title and optional info icon */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-[8px]">
            <div className="h-[32px] w-[32px] flex items-center justify-center squircle-mask squircle-outline-[0] bg-selected-gray/80">
              <RiBarChart2Fill className="h-4 w-4 text-brand-primary-800" />
            </div>

            <h3 className="text-[12px] font-medium text-brand-primary-new leading-[24px]">
              Competitor Overview
            </h3>

            <Tooltip>
              <TooltipTrigger asChild>
                <IconInfoCircle size={14} className="text-brand-primary-400" />
              </TooltipTrigger>
              <TooltipContent side="bottom" align="start" className="max-w-[300px]">
                <p>
                  See competitors’ ad mix, trend by brand, and <br /> most-used landing
                  pages.
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="flex gap-[6px]">
            {/* Overview Type Selector */}
            {overviewData && overviewData.length > 1 && (
              <div className="flex items-center gap-2">
                <SelectDropdown
                  options={overviewData.map((item) => ({
                    key: item.key,
                    label: item.label,
                    value: item.key,
                  }))}
                  selected={selectedOverviewItem}
                  onChange={(value) => {
                    setSelectedOverviewItem(value);
                    localStorage.setItem("competitor-overview-selected-item", value);
                  }}
                  placeholder="Select overview type"
                  className=""
                />
              </div>
            )}
            {/* Date Selector */}
            <div className="flex items-center gap-2 relative">
              <ReportDateSelector
                selectedDate={selectedDate}
                onChangeApply={onDateChange}
                disabled={loading}
                timezone={selectedAccount?.time_zone}
              />
              {showNudgeDot && (
                <div className="absolute -top-[2.5px] -right-[2.5px] h-[10px] w-[10px] rounded-full bg-[#FF6A33]" />
              )}
            </div>
            <SqButton
              size={"md"}
              variant={"outline"}
              className="w-8"
              onClick={toggleExpanded}
            >
              <RiArrowDownWideLine
                size={14}
                className={`text-brand-primary-400 transition-transform duration-200 shrink-0 ${
                  isExpanded ? "rotate-180" : "rotate-0"
                }`}
              />
            </SqButton>
          </div>
        </div>

        {/* Content area with smooth transition */}
        <div className={` ${isExpanded ? "flex flex-col gap-4" : "hidden"}`}>
          {/* Overview Content */}
          <div className="overflow-auto">
            {loading ? (
              <div className="h-[267px] flex items-center justify-center py-8">
                <WaveLoader className="w-[100px]" />
              </div>
            ) : overviewData ? (
              <div className="space-y-6">
                {(() => {
                  const selectedItem = overviewData.find(
                    (item) => item.key === selectedOverviewItem,
                  );
                  if (!selectedItem) return null;

                  return (
                    <div key={selectedItem.key} className="space-y-4">
                      {/* Section Content */}
                      {selectedItem.data && Object.keys(selectedItem.data).length > 0 ? (
                        <div className="flex gap-2 h-[267px]">
                          {/* Ad Distribution */}
                          {selectedItem.data.ad_distribution?.map((distribution) => (
                            <ActiveAdsSection
                              key={distribution.key}
                              distribution={distribution}
                            />
                          ))}
                          {/* Graph Data */}
                          {selectedItem.data.graph_data && (
                            <GraphDataSection
                              graphData={selectedItem.data.graph_data}
                              dataKey={selectedItem.key}
                              brands={brands}
                              timezone={selectedAccount?.time_zone}
                            />
                          )}

                          {/* Landing Pages */}
                          {selectedItem.data.landing_pages &&
                            selectedItem.data.landing_pages.length > 0 && (
                              <LandingPagesSection
                                landingPages={selectedItem.data.landing_pages}
                              />
                            )}
                        </div>
                      ) : (
                        <div className="h-[267px] flex items-center justify-center py-6 ">
                          <CompetitorsEmpty />
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div className="h-[267px] flex items-center justify-center py-8">
                <CompetitorsEmpty />
              </div>
            )}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

// Custom comparison function for memo to prevent unnecessary re-renders during polling
const arePropsEqual = (prevProps: Props, nextProps: Props) => {
  // Check if selectedBrandIds actually changed (not just reference)
  const selectedBrandIdsChanged =
    prevProps.selectedBrandIds.length !== nextProps.selectedBrandIds.length ||
    prevProps.selectedBrandIds.some(
      (id, index) => id !== nextProps.selectedBrandIds[index],
    );

  if (selectedBrandIdsChanged) {
    return false;
  }
  // For other props, do shallow comparison
  return (
    prevProps.filters === nextProps.filters &&
    prevProps.location === nextProps.location &&
    prevProps.selectedDate === nextProps.selectedDate &&
    prevProps.onDateChange === nextProps.onDateChange &&
    prevProps.refetchToken === nextProps.refetchToken
  );
};

export default memo(CompetitorAdsOverview, arePropsEqual);
